/**
 * CreateAgentPage Component
 *
 * This component provides a form interface for creating new agents in the system.
 * It includes features for:
 * - Form validation and error handling
 * - Input sanitization (especially for phone numbers)
 * - Success/error message display
 * - Responsive form layout
 *
 * The component manages form state and handles user input for various agent details
 * including personal information, contact details, and jurisdiction.
 */

import React, { useState } from "react";
import TopBar from "./Components/TopBar";
import { AddAgentRequest } from "@frontend/Models/Agent/AgentRequestResponseModels";
import "./CreateAgentPage.css";

const CreateAgentPage: React.FC = () => {
  /**
   * Form state management
   * Stores all form field values with their initial empty states
   */
  const [formData, setFormData] = useState<AddAgentRequest>({
    FirstName: "",
    LastName: "",
    Company: "",
    Email: "",
    Phone: "",
    Address: "",
    Jurisdiction: ""
  });

  // State for handling form submission feedback
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  /**
   * Generic input change handler
   * Updates the form state when any input field changes
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input field
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  /**
   * Phone number input validation
   * Ensures only numeric input is allowed for the phone field
   * Allows special keys like backspace, delete, and arrow keys
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} e - The keydown event from the phone input
   */
  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    // Allow backspace, delete, and arrow keys
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(key)) {
      return;
    }

    // Prevent the user from typing anything that's not a number
    if (!/^\d$/.test(key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="agents-Page">
      {/* TopBar component containing form submission logic */}
      <TopBar formData={formData} setError={setError} setFormData={setFormData} setSuccess={setSuccess}></TopBar>

      {/* Main form container with centered layout */}
      <div className="agents-entire-form-container">
        <div className="agents-form-container">
          {/* Feedback messages for form submission */}
          {success && <div className="success-message">{success}</div>}
          {error && <div className="error-message">{error}</div>}

          {/* Form fields for agent details */}
          <div className="agents-form-group">
            <label className="agents-form-group-label">First name</label>
            <div className="agents-input-icon">
              <input
                type="text"
                name="FirstName"
                placeholder="Enter first name"
                value={formData.FirstName}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="agents-form-group">
            <label className="agents-form-group-label">Last name</label>
            <div className="agents-input-icon">
              <input
                type="text"
                name="LastName"
                placeholder="Enter last name"
                value={formData.LastName}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="agents-form-group">
            <label className="agents-form-group-label">Company</label>
            <div className="agents-input-icon">
              <input
                type="text"
                name="Company"
                placeholder="Enter company"
                value={formData.Company}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="agents-form-group">
            <label className="agents-form-group-label">Email</label>
            <div className="agents-input-icon">
              <input
                type="email"
                name="Email"
                placeholder="Enter email"
                value={formData.Email}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="agents-form-group">
            <label className="agents-form-group-label">Phone</label>
            <div className="agents-input-icon">
              <input
                type="tel"
                name="Phone"
                placeholder="Enter phone"
                value={formData.Phone}
                onChange={handleInputChange}
                onKeyDown={handlePhoneKeyDown}
                pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}" // Phone format: XXX-XXX-XXXX
                maxLength={14}
              />
            </div>
          </div>

          <div className="agents-form-group">
            <label className="agents-form-group-label">Address</label>
            <div className="agents-input-icon">
              <input
                type="text"
                name="Address"
                placeholder="Enter address"
                value={formData.Address}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="agents-form-group">
            <label className="agents-form-group-label">Jurisdiction</label>
            <div className="agents-input-icon">
              <input
                type="text"
                name="Jurisdiction"
                placeholder="Enter Jurisdiction"
                value={formData.Jurisdiction}
                onChange={handleInputChange}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CreateAgentPage;
